import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  message,
  Tag,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Divider,
  Popconfirm,
  Typography,
  Drawer,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  PrinterOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { invoiceService, productService } from '../../services';
import { formatNumber, formatDate, formatCurrency } from '../../utils/helpers';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TextArea } = Input;

const ImportInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    dateRange: [],
    status: 'all',
  });

  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();

  useEffect(() => {
    loadInvoices();
    loadProducts();
  }, [filters]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.dateRange.length === 2) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.status !== 'all') params.status = filters.status;

      const response = await invoiceService.import.getAll(params);
      if (response.success) {
        setInvoices(response.data || []);
      }
    } catch (error) {
      //console.error('Error loading import invoices:', error);
      message.error('Không thể tải danh sách phiếu nhập');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getFull();
      if (response.success) {
        setProducts(response.data || []);
      }
    } catch (error) {
      //console.error('Error loading products:', error);
    }
  };

  const handleCreate = () => {
    setEditingInvoice(null);
    form.resetFields();
    form.setFieldsValue({
      invoice_date: dayjs(),
      status: 'draft',
      details: [{ product_id: null, variant_id: null, quantity: 1, unit_price: 0 }],
    });
    setModalVisible(true);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    form.setFieldsValue({
      ...invoice,
      invoice_date: dayjs(invoice.invoice_date),
    });
    setModalVisible(true);
  };

  const handleView = async (invoice) => {
    setCurrentInvoice(invoice);
    try {
      const response = await invoiceService.import.getById(invoice.invoice_id);
      if (response.success) {
        setInvoiceDetails(response.data?.details || []);
      }
    } catch (error) {
      //console.error('Error loading invoice details:', error);
      setInvoiceDetails([]);
    }
    setDetailDrawerVisible(true);
  };

  const handleDelete = async (invoiceId) => {
    try {
      const response = await invoiceService.import.delete(invoiceId);
      if (response.success) {
        message.success('Xóa phiếu nhập thành công');
        loadInvoices();
      } else {
        message.error(response.message || 'Xóa phiếu nhập thất bại');
      }
    } catch (error) {
      //console.error('Error deleting invoice:', error);
      message.error('Xóa phiếu nhập thất bại');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const invoiceData = {
        ...values,
        invoice_date: values.invoice_date.format('YYYY-MM-DD'),
      };

      let response;
      if (editingInvoice) {
        response = await invoiceService.import.update(editingInvoice.invoice_id, invoiceData);
      } else {
        response = await invoiceService.import.create(invoiceData);
      }

      if (response.success) {
        message.success(editingInvoice ? 'Cập nhật phiếu nhập thành công' : 'Tạo phiếu nhập thành công');
        setModalVisible(false);
        loadInvoices();
      } else {
        message.error(response.message || 'Thao tác thất bại');
      }
    } catch (error) {
      //console.error('Error saving invoice:', error);
      message.error('Thao tác thất bại');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'draft': { color: 'default', text: 'Nháp' },
      'pending': { color: 'processing', text: 'Chờ duyệt' },
      'approved': { color: 'success', text: 'Đã duyệt' },
      'completed': { color: 'green', text: 'Hoàn thành' },
      'cancelled': { color: 'error', text: 'Đã hủy' },
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Mã phiếu nhập',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
      render: (id) => `PN${String(id).padStart(6, '0')}`,
      sorter: (a, b) => a.invoice_id - b.invoice_id,
    },
    {
      title: 'Ngày nhập',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date),
    },
    {
      title: 'Nhà cung cấp',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (name) => name || 'Chưa có',
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => formatCurrency(amount || 0),
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (note) => note || '-',
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
            title="Xem chi tiết"
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            title="Sửa"
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa phiếu nhập này?"
            onConfirm={() => handleDelete(record.invoice_id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              title="Xóa"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const detailColumns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.product_name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            SKU: {record.sku || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => formatNumber(quantity),
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price) => formatCurrency(price),
    },
    {
      title: 'Thành tiền',
      key: 'total',
      render: (_, record) => formatCurrency((record.quantity || 0) * (record.unit_price || 0)),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={3}>Quản lý phiếu nhập kho</Title>
        </div>

        {/* Bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Input
              placeholder="Tìm kiếm theo mã phiếu, nhà cung cấp..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <RangePicker
              placeholder={['Từ ngày', 'Đến ngày']}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates || [])}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Trạng thái"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
            >
              <Option value="all">Tất cả</Option>
              <Option value="draft">Nháp</Option>
              <Option value="pending">Chờ duyệt</Option>
              <Option value="approved">Đã duyệt</Option>
              <Option value="completed">Hoàn thành</Option>
              <Option value="cancelled">Đã hủy</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Tạo phiếu nhập
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadInvoices}
                loading={loading}
              />
            </Space>
          </Col>
        </Row>

        {/* Bảng danh sách */}
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="invoice_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} phiếu`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Modal tạo/sửa phiếu nhập */}
      <Modal
        title={editingInvoice ? 'Sửa phiếu nhập' : 'Tạo phiếu nhập mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="invoice_date"
                label="Ngày nhập"
                rules={[{ required: true, message: 'Vui lòng chọn ngày nhập' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="supplier_name"
                label="Nhà cung cấp"
                rules={[{ required: true, message: 'Vui lòng nhập tên nhà cung cấp' }]}
              >
                <Input placeholder="Nhập tên nhà cung cấp" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Trạng thái"
                rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
              >
                <Select>
                  <Option value="draft">Nháp</Option>
                  <Option value="pending">Chờ duyệt</Option>
                  <Option value="approved">Đã duyệt</Option>
                  <Option value="completed">Hoàn thành</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="total_amount"
                label="Tổng tiền"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => formatCurrency(value)}
                  parser={value => value.replace(/\D/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="note"
                label="Ghi chú"
              >
                <TextArea rows={3} placeholder="Nhập ghi chú (tùy chọn)" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit">
                {editingInvoice ? 'Cập nhật' : 'Tạo phiếu nhập'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Drawer chi tiết phiếu nhập */}
      <Drawer
        title={`Chi tiết phiếu nhập ${currentInvoice ? `PN${String(currentInvoice.invoice_id).padStart(6, '0')}` : ''}`}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        width={800}
      >
        {currentInvoice && (
          <div>
            {/* Thông tin phiếu nhập */}
            <Card title="Thông tin chung" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong>Mã phiếu nhập:</Text> PN{String(currentInvoice.invoice_id).padStart(6, '0')}
                </Col>
                <Col span={12}>
                  <Text strong>Ngày nhập:</Text> {formatDate(currentInvoice.invoice_date)}
                </Col>
                <Col span={12}>
                  <Text strong>Nhà cung cấp:</Text> {currentInvoice.supplier_name || 'Chưa có'}
                </Col>
                <Col span={12}>
                  <Text strong>Trạng thái:</Text> {getStatusTag(currentInvoice.status)}
                </Col>
                <Col span={12}>
                  <Text strong>Tổng tiền:</Text> {formatCurrency(currentInvoice.total_amount || 0)}
                </Col>
                <Col span={12}>
                  <Text strong>Người tạo:</Text> {currentInvoice.created_by_name}
                </Col>
                {currentInvoice.note && (
                  <Col span={24}>
                    <Text strong>Ghi chú:</Text> {currentInvoice.note}
                  </Col>
                )}
              </Row>
            </Card>

            {/* Chi tiết sản phẩm */}
            <Card title="Chi tiết sản phẩm" size="small">
              <Table
                columns={detailColumns}
                dataSource={invoiceDetails}
                rowKey="detail_id"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3} style={{ textAlign: 'right' }}>
                      <Text strong>Tổng cộng:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong>
                        {formatCurrency(
                          invoiceDetails.reduce(
                            (sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0),
                            0
                          )
                        )}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ImportInvoices;
